import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiShield, FiLock, FiEye, FiDatabase, FiUsers, FiGlobe, FiArrowUp } from 'react-icons/fi';
import './LegalPages.css';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="legal-page-wrapper">
      <div className="legal-page-header">
        <button className="back-button" onClick={() => navigate('/')}>
          <FiArrowLeft /> Back to Home
        </button>
        <div className="legal-page-title">
          <FiShield className="legal-icon" />
          <h1>Privacy Policy</h1>
        </div>
        <p className="legal-page-subtitle">
          This Privacy Policy describes how we collect, use, disclose, and manage your personal information when you use www.lumesolutions.co and its Services.
        </p>
        <p className="legal-last-updated">Last Updated: October 2025</p>
      </div>

      <div className="legal-content-container">
        <section className="legal-section">
          <div className="section-header">
            <FiDatabase className="section-icon" />
            <h2>1. Information We Collect</h2>
          </div>
          <p>We may collect:</p>
          <ul className="legal-list">
            <li><strong>Personal identifiers:</strong> name, email address, phone number, national ID, etc.</li>
            <li><strong>Contact details:</strong> postal address, billing address.</li>
            <li><strong>Usage data:</strong> IP address, device type, browser, pages visited, interaction data.</li>
            <li><strong>Transaction & payment data:</strong> billing, transaction history, payment method.</li>
            <li><strong>Communications:</strong> messages, support requests, feedback.</li>
            <li><strong>Other information:</strong> that you voluntarily provide to us.</li>
          </ul>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiEye className="section-icon" />
            <h2>2. Use of Information</h2>
          </div>
          <p>We use your data to:</p>
          <ul className="legal-list">
            <li>Provide and maintain the Services.</li>
            <li>Process payments and transactions.</li>
            <li>Communicate with you (support, notifications, marketing).</li>
            <li>Improve and personalize your experience.</li>
            <li>Conduct analytics and business intelligence.</li>
            <li>Detect fraud, enforce policies, and comply with legal obligations.</li>
          </ul>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiUsers className="section-icon" />
            <h2>3. Sharing & Disclosure</h2>
          </div>
          <p>We may share your data with:</p>
          <ul className="legal-list">
            <li>Service providers and contractors (payment processors, hosting, analytics).</li>
            <li>Legal or regulatory authorities when required by law.</li>
            <li>Third parties with your consent.</li>
          </ul>
          <div className="highlight-box">
            <p><strong>We will not sell your personal data to third parties.</strong></p>
          </div>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiDatabase className="section-icon" />
            <h2>4. Data Retention</h2>
          </div>
          <p>
            We keep your personal information only as long as necessary for the purposes in this Policy, 
            unless longer retention is required by law. When no longer needed, we will securely delete or anonymize it.
          </p>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiShield className="section-icon" />
            <h2>5. Your Rights</h2>
          </div>
          <p>Subject to applicable law, you may have rights including:</p>
          <ul className="legal-list">
            <li>Access to your personal information.</li>
            <li>Correction of inaccurate data.</li>
            <li>Deletion or erasure (where lawful).</li>
            <li>Withdrawal of consent for certain processing.</li>
            <li>Objection to processing (especially for marketing).</li>
            <li>Data portability.</li>
            <li>Lodge a complaint with a supervisory authority.</li>
          </ul>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiLock className="section-icon" />
            <h2>6. Security</h2>
          </div>
          <p>
            We implement technical and organizational safeguards (e.g. encryption, access controls) to protect 
            your personal data. However, we cannot guarantee absolute security.
          </p>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiEye className="section-icon" />
            <h2>7. Cookies & Tracking</h2>
          </div>
          <p>
            We use cookies and similar technologies to collect and store information about your use of the Site. 
            You can disable or manage cookies via your browser, though some features may not function properly.
          </p>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiUsers className="section-icon" />
            <h2>8. Children's Privacy</h2>
          </div>
          <p>
            Our Services are not intended for individuals under 18. We do not knowingly collect data from minors. 
            If you believe a minor has provided us with data, contact us to request deletion.
          </p>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiGlobe className="section-icon" />
            <h2>9. International Transfers</h2>
          </div>
          <p>
            Your data may be processed or stored in jurisdictions other than yours. Where so, we make efforts 
            to ensure adequate safeguards in line with applicable laws.
          </p>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiShield className="section-icon" />
            <h2>10. Changes to This Policy</h2>
          </div>
          <p>
            We may update this Privacy Policy periodically. Changes will be posted on the Site with the new 
            "Last Updated" date. Continued use constitutes acceptance of those changes.
          </p>
        </section>

        <section className="legal-section contact-section">
          <div className="section-header">
            <FiUsers className="section-icon" />
            <h2>11. Contact Us</h2>
          </div>
          <p>For privacy or data protection queries, you may contact:</p>
          <div className="contact-box">
            <h3>Lume Solutions Limited</h3>
            <p>8th Floor, Reliable Towers, Mogotio Road, Westlands, Nairobi</p>
            <p>P.O BOX 26541-00504, Nairobi Kenya</p>
            <p>
              <strong>Email:</strong> <a href="mailto:info@lumesolutions.co">info@lumesolutions.co</a>
            </p>
            <p><strong>Phone:</strong> +254 716 060 606</p>
          </div>
        </section>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          className="back-to-top-btn"
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          <FiArrowUp />
        </button>
      )}
    </div>
  );
};

export default PrivacyPolicy;